// Shared helpers for the GraphBuilder /api/pipeline/run payload.
//
// Both the Test Graph chat page and the Test Cases bulk-run page build
// the same POST body and persist their settings to the same localStorage
// key, so a tweak in either surface affects the other.

export const TEST_GRAPH_SETTINGS_LS_KEY = (graphId) =>
  `testGraph_settings_${graphId}`;

export const DEFAULT_TEST_GRAPH_SETTINGS = {
  execution_mode: 'BUILD_AND_RUN',
  workspace_id: '',
  graph_id: '',
  min_similarity: 0.5,
  snn_execution_mode: 'compiled_full_numpy',
  use_cache: true,
  similarity_top_k: 150,
  decompose_query: true,
  nl_prompt: '',
  focus_areas: '',
  llm_model: '',
  model_preset: 'balanced',
  stage_models: {},
  enable_abstract_query_handler: true,
  max_gap_fill_iterations: 2,
  graph_traversal_depth: 2,
  include_execution_trace: true,
  stream_response: true,
  expand_with_vocabulary: true,
  enable_cross_encoder_rerank: false,
  simulate_duration_ms: 200,
  simulate_step_ms: 20,
  max_recursive_ticks: 10,
  bias_hz: 50,
  gain: 20,
  min_background_rate_hz: 0,
  rate_coding_min_hz: 8,
  rate_coding_max_hz: 220,
  rate_coding_base_hz: 50,
  softmax_temperature: 0.35,
  context_potency_mode: 'auto',
  context_potency_threshold: 0.26,
  auto_potency_boost_multiplier: 1.3,
  include_verbatim_nodes: true,
  verbatim_inclusion_mode: 'auto',
  enable_judge: true,
  enable_context_distillation: true,
  mlcf_enabled: true,
  mlcf_identity_filter: true,
  mlcf_scope_filter: true,
  mlcf_constraint_filter: true,
  mlcf_context_filter: true,
  mlcf_structural_filter: true,
  mlcf_identity_threshold: 0.5,
  mlcf_scope_overlap_threshold: 0.3,
  mlcf_context_strictness: 'moderate',
  mlcf_min_survivors: 1,
  crs_enabled: true,
  crs_semantic_weight: 0.25,
  crs_node_type_compat_weight: 0.15,
  crs_intent_affinity_weight: 0.15,
  crs_scope_intersection_weight: 0.15,
  crs_causal_modality_weight: 0.10,
  crs_authority_weight: 0.08,
  crs_gate_readiness_weight: 0.07,
  crs_exception_alignment_weight: 0.05,
  crs_evidence_threshold: 0.3,
  crs_default_unscored_relevance: 0.3,
  propagation_evidence_threshold: 0.15,
  enable_ser: true,
  ser_temperature: 0.35,
  ser_bm25_boost: 0.1,
  enable_query_reformulation: false,
  compact_render_mode: true,
  capture_synapse_fire_stats: true,
  compact_max_claims: 100,
  compact_min_potency: 0.10,
  answer_max_tokens: null,
};

// Settings that are forcibly locked when the Test Graph chat runs inside
// the public embed flow (DM, the rest of the RCMBoost suite, and AskCm).
// These win over both DEFAULT_TEST_GRAPH_SETTINGS and any stale
// localStorage values for the embedded graph, but they do not affect the
// CM direct UI (where isEmbedMode() is false).
export const EMBED_FORCED_SETTINGS = {
  stream_response: true,
  snn_execution_mode: 'compiled_full_numpy',
  expand_with_vocabulary: true,
  decompose_query: true,
  min_similarity: 0.5,
  similarity_top_k: 150,
  context_potency_mode: 'auto',
  auto_potency_boost_multiplier: 0.5,
  enable_ser: true,
};

export function applyEmbedForcedSettings(settings) {
  return { ...(settings || {}), ...EMBED_FORCED_SETTINGS };
}

export function loadTestGraphSettings(graphId) {
  if (!graphId) return { ...DEFAULT_TEST_GRAPH_SETTINGS };
  try {
    const raw = localStorage.getItem(TEST_GRAPH_SETTINGS_LS_KEY(graphId));
    if (raw) {
      const parsed = JSON.parse(raw);
      const merged = {
        ...DEFAULT_TEST_GRAPH_SETTINGS,
        ...parsed,
        graph_id: graphId,
      };
      // Backwards compat: pre-Auto users have a numeric CPT but no
      // context_potency_mode persisted. Honour their explicit choice by
      // defaulting them to manual so behaviour does not silently change.
      if (
        parsed &&
        parsed.context_potency_mode === undefined &&
        typeof parsed.context_potency_threshold === 'number'
      ) {
        merged.context_potency_mode = 'manual';
      }
      return merged;
    }
  } catch (e) {
    console.warn('[pipelineRunPayload] Failed to parse saved settings:', e);
  }
  return { ...DEFAULT_TEST_GRAPH_SETTINGS, graph_id: graphId };
}

export function saveTestGraphSettings(graphId, settings) {
  if (!graphId) return;
  try {
    localStorage.setItem(
      TEST_GRAPH_SETTINGS_LS_KEY(graphId),
      JSON.stringify(settings || {}),
    );
  } catch (e) {
    console.warn('[pipelineRunPayload] Failed to persist settings:', e);
  }
}

// Normalizes a "focus areas" value into a string[] for the backend
// (PipelineRunRequest.focus_areas / SNNPipelineConfig.focus_areas).
// Accepts a newline/comma-separated string or an existing array; returns
// undefined when empty so the field is omitted from the payload.
export function parseFocusAreas(value) {
  let list = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (typeof value === 'string') {
    list = value.split(/[\n,]/);
  } else {
    return undefined;
  }
  const cleaned = list
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s.length > 0);
  return cleaned.length > 0 ? cleaned : undefined;
}

// Parses `provider::providerId::rawModel` into its components.
// When the model is not in `provider::` form, returns the raw string
// as `rawModel` and `providerId: undefined`.
export function resolveLlmProvider(settings) {
  const llmModel = settings?.llm_model;
  if (typeof llmModel === 'string' && llmModel.startsWith('provider::')) {
    const parts = llmModel.split('::');
    return { providerId: parts[1], rawModel: parts[2] };
  }
  return { providerId: undefined, rawModel: llmModel || undefined };
}

// Builds the GraphBuilder /api/pipeline/run payload from a settings
// object. Per-case execution_config overrides are merged on top of
// settings before payload construction. `extras` may carry chat-specific
// fields (conversation_history, last_snn_context, session_id, overrides)
// that the Test Cases bulk-run path leaves undefined.
export function buildPipelineRunPayload({
  settings,
  nl_query,
  graph_id,
  workspace_id,
  providerId,
  effectiveLlmModel,
  conversation_history,
  last_snn_context,
  session_id,
  perCaseConfig,
  overrides,
} = {}) {
  const merged = { ...(settings || {}), ...(perCaseConfig || {}) };

  let resolvedProviderId = providerId;
  let resolvedLlmModel = effectiveLlmModel;
  if (resolvedProviderId === undefined && resolvedLlmModel === undefined) {
    const r = resolveLlmProvider(merged);
    resolvedProviderId = r.providerId;
    resolvedLlmModel = r.rawModel;
  }

  const trimmedHistory = Array.isArray(conversation_history)
    ? conversation_history
    : undefined;

  const payload = {
    nl_query,
    execution_mode: merged.execution_mode,
    workspace_id: workspace_id || merged.workspace_id || undefined,
    graph_id: graph_id || merged.graph_id || undefined,
    min_similarity: merged.min_similarity || undefined,
    snn_execution_mode: merged.snn_execution_mode,
    similarity_top_k: merged.similarity_top_k || undefined,
    decompose_query: merged.decompose_query,
    nl_prompt: merged.nl_prompt || undefined,
    focus_areas: parseFocusAreas(merged.focus_areas),
    conversation_history:
      trimmedHistory && trimmedHistory.length > 0 ? trimmedHistory : undefined,
    last_snn_context: last_snn_context || undefined,
    session_id: session_id || undefined,
    llm_model: resolvedLlmModel || undefined,
    stage_models: merged.stage_models || undefined,
    provider_id: resolvedProviderId || undefined,
    enable_abstract_query_handler: merged.enable_abstract_query_handler,
    max_gap_fill_iterations: merged.max_gap_fill_iterations || undefined,
    graph_traversal_depth: merged.graph_traversal_depth || undefined,
    include_execution_trace: merged.include_execution_trace,
    expand_with_vocabulary: merged.expand_with_vocabulary,
    enable_cross_encoder_rerank: merged.enable_cross_encoder_rerank,
    simulate_duration_ms: merged.simulate_duration_ms || undefined,
    simulate_step_ms: merged.simulate_step_ms || undefined,
    max_recursive_ticks: merged.max_recursive_ticks || undefined,
    bias_hz: merged.bias_hz,
    gain: merged.gain,
    min_background_rate_hz: merged.min_background_rate_hz || undefined,
    rate_coding_min_hz: merged.rate_coding_min_hz,
    rate_coding_max_hz: merged.rate_coding_max_hz,
    rate_coding_base_hz: merged.rate_coding_base_hz,
    softmax_temperature: merged.softmax_temperature,
    // Context potency: when mode is "auto", omit the static threshold so the
    // backend's dynamic selector (knee/p65/top-K floor + judge escalation)
    // picks a per-query threshold. In "manual" mode, pass the numeric value
    // through as before so the backend uses it verbatim.
    context_potency_mode:
      merged.context_potency_mode === 'auto' ||
      merged.context_potency_mode === 'manual'
        ? merged.context_potency_mode
        : undefined,
    context_potency_threshold:
      merged.context_potency_mode === 'auto'
        ? undefined
        : merged.context_potency_threshold || undefined,
    // Boost multiplier (raw_chosen * boost) -- only honoured by the
    // backend when context_potency_mode == 'auto'. Sent as undefined in
    // manual mode so the backend default (1.9) is harmless.
    auto_potency_boost_multiplier:
      merged.context_potency_mode === 'auto' &&
      Number.isFinite(Number(merged.auto_potency_boost_multiplier))
        ? Number(merged.auto_potency_boost_multiplier)
        : undefined,
    include_verbatim_nodes: merged.include_verbatim_nodes,
    verbatim_inclusion_mode: merged.verbatim_inclusion_mode || undefined,
    enable_judge: merged.enable_judge,
    enable_context_distillation: merged.enable_context_distillation,
    mlcf_enabled: merged.mlcf_enabled,
    mlcf_identity_filter: merged.mlcf_identity_filter,
    mlcf_scope_filter: merged.mlcf_scope_filter,
    mlcf_constraint_filter: merged.mlcf_constraint_filter,
    mlcf_context_filter: merged.mlcf_context_filter,
    mlcf_structural_filter: merged.mlcf_structural_filter,
    mlcf_identity_threshold: merged.mlcf_identity_threshold,
    mlcf_scope_overlap_threshold: merged.mlcf_scope_overlap_threshold,
    mlcf_context_strictness: merged.mlcf_context_strictness,
    mlcf_min_survivors: merged.mlcf_min_survivors,
    crs_enabled: merged.crs_enabled,
    crs_semantic_weight: merged.crs_semantic_weight,
    crs_node_type_compat_weight: merged.crs_node_type_compat_weight,
    crs_intent_affinity_weight: merged.crs_intent_affinity_weight,
    crs_scope_intersection_weight: merged.crs_scope_intersection_weight,
    crs_causal_modality_weight: merged.crs_causal_modality_weight,
    crs_authority_weight: merged.crs_authority_weight,
    crs_gate_readiness_weight: merged.crs_gate_readiness_weight,
    crs_exception_alignment_weight: merged.crs_exception_alignment_weight,
    crs_evidence_threshold: merged.crs_evidence_threshold,
    crs_default_unscored_relevance: merged.crs_default_unscored_relevance,
    propagation_evidence_threshold: merged.propagation_evidence_threshold,
    enable_ser: merged.enable_ser,
    ser_temperature: merged.enable_ser ? merged.ser_temperature : undefined,
    ser_bm25_boost: merged.enable_ser ? merged.ser_bm25_boost : undefined,
    use_cache: merged.use_cache || false,
    router_graph_id: merged.router_graph_id || undefined,
    router_mode: merged.multi_graph_enabled
      ? 'route_and_execute_multi'
      : (merged.router_mode || undefined),
    multi_graph_threshold: merged.multi_graph_enabled
      ? 1.0 - (merged.multi_graph_threshold_pct ?? 30) / 100
      : undefined,
    pre_enrich_process_ids:
      Array.isArray(merged.pre_enrich_process_ids) && merged.pre_enrich_process_ids.length > 0
        ? merged.pre_enrich_process_ids
        : undefined,
    enable_nl_to_json:
      merged.enable_nl_to_json === true ? true : undefined,
    enable_query_reformulation:
      merged.enable_query_reformulation === true
        ? true
        : merged.enable_query_reformulation === false
        ? false
        : undefined,
    // FGE - Optimized (compact render) toggle. When ON the GraphBuilder
    // builds the compact evidence bundle (claim_compact + SPO + synapse-fire
    // weights) and uses the render-only Llama prompt instead of the legacy
    // prioritized bundle. capture_synapse_fire_stats is auto-enabled with
    // it so the SNN executor exports per-synapse firing stats that feed
    // the compact bundle's potency scoring.
    compact_render_mode: merged.compact_render_mode === true,
    capture_synapse_fire_stats:
      merged.compact_render_mode === true ||
      merged.capture_synapse_fire_stats === true,
    compact_max_claims:
      typeof merged.compact_max_claims === 'number' && merged.compact_max_claims > 0
        ? merged.compact_max_claims
        : undefined,
    compact_min_potency:
      typeof merged.compact_min_potency === 'number' && merged.compact_min_potency >= 0
        ? merged.compact_min_potency
        : undefined,
    answer_max_tokens:
      typeof merged.answer_max_tokens === 'number' && merged.answer_max_tokens >= 64
        ? merged.answer_max_tokens
        : undefined,
  };

  // When using a router in route_and_execute mode, let the router
  // decide the graph (matches TestGraphPage behaviour).
  if (payload.router_graph_id && payload.router_mode !== 'route_only') {
    payload.graph_id = null;
  }

  // Optional re-execution overrides (used by chat after ambiguous
  // routing selection). Test Cases bulk run leaves overrides undefined.
  if (overrides?.skip_router) {
    payload.router_graph_id = undefined;
    payload.router_mode = undefined;
    if (overrides.process_id) {
      payload.process_id = overrides.process_id;
      payload.process_query_input_key =
        overrides.query_input_key || 'nl_query';
      payload.graph_id = undefined;
    } else if (overrides.graph_id) {
      payload.graph_id = overrides.graph_id;
    }
  }

  return payload;
}
